import React, { Suspense, useEffect } from "react";
import "./App.css";
import { CssBaseline, ThemeProvider } from "@mui/material";
import RenderRoutes from "./utils/router";
import appRoutes from "./Routes";
import DataLayer from "./DataLayer";
import { PostHogProvider } from "posthog-js/react";

import { ColorModeContext, useMode } from "./theme";
import WishlinkNotification from "./WishlinkComponents/WishlinkNotification";
import LoadingPage from "./components/Fallback/LoadingPage";
import config, { getAppBuildVersion, getPlatform } from "./utils/config";
import PullToRefresh from "pulltorefreshjs";
import {
  FORCE_UPDATE_BUILD_NUMBER,
  HOT_REFRESH_SW_VERSION,
  PAGE_URLS,
} from "./utils/constants";
import BrandPageOverlay from "./components/Brands/BrandPageOverlay";

import RecommendedCollectionsDataLayer from "./RecommendedCollectionsStore/RecommendedCollectionsDataLayer";
import CollectionsDataLayer from "./sourcing/views/Collections/collectionsDataLayer";
import CartDataLayer from "./sourcing/views/Cart/cartDataLayer";
import MyOrdersDataLayer from "./sourcing/views/MyOrders/myOrdersDataLayer";
import ProductDataLayer from "./sourcing/views/Product/productDataLayer";
import HomeDataLayer from "./sourcing/views/Home/homeDataLayer";
import CreditsDataLayer from "./sourcing/views/Credits/creditsDataLayer";
import YoutubeShoppingDataLayer from "./YoutubeShoppingStore/YoutubeShoppingDataLayer";
import ProfileDataLayer from "./ProfileDataLayer/ProfileDataLayer";
import HomePageDataLayer from "./HomePageDataLayer/HomePageDataLayer";
import BrandsPageDataLayer from "./BrandsPageDataLayer/BrandsPageDataLayer";
import WebEngageScript from "./components/WebEngageScript";
import { AppForceUpdate } from "./components/AppForceUpdate";
import NewPostDrawer from "./components/NewPostDrawer/NewPostDrawer";
import LoadScripts from "./components/LoadScripts";
import { useBlitzllama } from "./hooks/Blitzllama/useBlitzllama";
import { unregister } from "./serviceWorkerRegistration";
import { useGetServiceWorkerVersionQuery } from "./HomePageDataLayer/HomePageReduxAPI";
import { setUserPropertiesGa } from "./utils/gaUtils";
import FirebaseWrapper from "FirebaseWrapper";
import UnderMaintenance from "views/UnderMaintenance";
import { FEATURE_FLAGS } from "utils/firebaseFeatureFlags";
import { useFirebaseFeatureFlag } from "hooks/useFirebaseFeatureFlag";
import useClarity from "hooks/useClarity";
import FTUXBootstrapper from "components/FTUXBootstrapper";

// import DownloadAppNudge from "components/DownloadAppNudge";
const options = {
  api_host: config.API.postHogHost,
  enable_recording_console_log: true,
  autocapture: false,
  disable_session_recording: true,
};

const shouldPullToRefresh = () => {
  let shouldRefresh = !window.scrollY;
  const allDrawers = Array.from(
    document.getElementsByClassName("MuiDrawer-root")
  );
  const openedDrawer = allDrawers.find(
    (drawer) => !drawer.className.includes("MuiModal-hidden")
  );
  const allModals = Array.from(
    document.getElementsByClassName("MuiModal-root")
  );
  const openedModal = allModals.find(
    (modal) => !modal.className.includes("MuiModal-hidden")
  );
  // Check for opened bottom sheets
  const allBottomSheets = Array.from(
    document.getElementsByClassName("bottom-sheet-wrapper")
  );
  const openedBottomSheet = allBottomSheets.find(
    (sheet) => !sheet.className.includes("bottom-sheet-hidden")
  );
  if (openedDrawer || openedModal || openedBottomSheet) shouldRefresh = false;
  if (
    window.location.pathname === PAGE_URLS.COLLAGE_CREATION ||
    window.location.pathname === PAGE_URLS.ERROR_PAGE ||
    window.location.pathname.startsWith("/copy-links/") ||
    window.location.pathname.endsWith("/preview-links") ||
    window.location.pathname.startsWith("/paid-collaboration-chat/")
  )
    shouldRefresh = false;
  return shouldRefresh;
};

const PullToRefreshComponent = () => {
  useEffect(() => {
    PullToRefresh.destroyAll();
    PullToRefresh.init({
      mainElement: "body",
      shouldPullToRefresh: shouldPullToRefresh,
      refreshTimeout: 100,
      onRefresh: () => window.location.reload(),
      distThreshold: 75,
      distReload: 0,
      distIgnore: 10,
      distMax: 100,
    });
  }, []);
  return <></>;
};

const BtilzllamaComponent = () => {
  const { initializeBlitzllama } = useBlitzllama();
  useEffect(() => {
    initializeBlitzllama();
  }, []);
  return <></>;
};

const HotRefreshServiceWorker = () => {
  const { data: versionData } = useGetServiceWorkerVersionQuery({
    version: HOT_REFRESH_SW_VERSION,
  });
  useEffect(() => {
    if (versionData && versionData.success && versionData.update) {
      unregister();
    }
  }, [versionData]);
  return <></>;
};

function App() {
  const [theme, colorMode] = useMode();
  const { flagValue, isLoading } = useFirebaseFeatureFlag(
    FEATURE_FLAGS.APP_UNDER_MAINTENANCE
  );
  const isUnderMaintenance =
    isLoading === false &&
    typeof flagValue === "string" &&
    flagValue?.toLowerCase() === "true";

  useClarity();

  const appBuildVersion = getAppBuildVersion();
  const platform = getPlatform();
  const isForceUpdateAvailable =
    Number.isInteger(appBuildVersion) &&
    appBuildVersion < FORCE_UPDATE_BUILD_NUMBER &&
    platform === "android";

  useEffect(() => {
    setUserPropertiesGa();
  }, []);

  return (
    <>
      <PostHogProvider apiKey={config.API.postHogKey} options={options}>
        <ColorModeContext.Provider value={colorMode}>
          {isUnderMaintenance ? (
            <UnderMaintenance />
          ) : (
            <FirebaseWrapper>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <PullToRefreshComponent />
                <BtilzllamaComponent />
                <HotRefreshServiceWorker />
                <FTUXBootstrapper />
                <div className="App" style={{ minHeight: window.innerHeight }}>
                  {/* <DownloadAppNudge /> */}
                  {isForceUpdateAvailable ? (
                    <AppForceUpdate
                      isForceUpdateAvailable={isForceUpdateAvailable}
                    />
                  ) : (
                    <>
                      <DataLayer>
                        <Suspense fallback={<LoadingPage />}>
                          <RenderRoutes routes={appRoutes} />
                        </Suspense>
                      </DataLayer>
                      <NewPostDrawer />
                      <BrandPageOverlay />
                      <WishlinkNotification />
                      <CollectionsDataLayer />
                      <HomeDataLayer />
                      <CartDataLayer />
                      <MyOrdersDataLayer />
                      <ProductDataLayer />
                      <CreditsDataLayer />
                      <RecommendedCollectionsDataLayer />
                      <YoutubeShoppingDataLayer />
                      <ProfileDataLayer />
                      <HomePageDataLayer />
                      <BrandsPageDataLayer />
                      {/* Please fix this */}
                      <WebEngageScript />
                      <LoadScripts />
                    </>
                  )}
                </div>
              </ThemeProvider>
            </FirebaseWrapper>
          )}
        </ColorModeContext.Provider>
      </PostHogProvider>
    </>
  );
}

export default App;
